import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Select from "react-select";
import { IoCloseOutline } from "react-icons/io5";
import { IoMdArrowBack } from "react-icons/io";
import API_URL from "../../src/Config";
import "../forms/voyageForm.css";
import "./excavationReviewerConfiguration.css";
import {
  EXCAVATION_PERMIT_LABEL,
  buildApprovalPayload,
  buildApprovalRows,
  buildDepartmentOptions,
  emptyApprovalRow,
  findExcavationPermitType,
  validateApprovalRows,
} from "./excavationApprovalSetupUtils";

function EditExcavationDepartmentApprovalSetup({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const { permitTypeId } = useParams();
  const [permitOption, setPermitOption] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [rows, setRows] = useState([emptyApprovalRow()]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const showSnackbar = useCallback((message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  }, []);

  const fetchSetup = useCallback(async () => {
    try {
      setPageLoading(true);
      const authKey = localStorage.getItem("authKey");
      const [permitResponse, departmentResponse] = await Promise.all([
        axios.get(`${API_URL}/api/get_work_permit_types`, {
          headers: { Authorization: authKey },
        }),
        axios.get(`${API_URL}/api/get_departments`, {
          headers: { Authorization: authKey },
        }),
      ]);

      const excavationPermit =
        (permitTypeId &&
          (permitResponse.data?.work_permit_types || []).find(
            (permitType) => permitType.id === permitTypeId
          )) ||
        findExcavationPermitType(permitResponse.data?.work_permit_types || []);

      if (!excavationPermit) {
        setSeverity("error");
        showSnackbar("Excavation permit type was not found.");
        return;
      }

      const option = {
        value: excavationPermit.id,
        label: EXCAVATION_PERMIT_LABEL,
      };

      setPermitOption(option);
      setDepartmentOptions(buildDepartmentOptions(departmentResponse.data || []));

      const setupResponse = await axios.get(
        `${API_URL}/api/permit/departments/setup/${option.value}`,
        { headers: { Authorization: authKey } }
      );

      setRows(buildApprovalRows(setupResponse.data?.departments || []));
    } catch (error) {
      console.error("Error fetching excavation approval setup:", error.message);
      setSeverity("error");
      showSnackbar("Unable to fetch department approval setup.");
    } finally {
      setPageLoading(false);
    }
  }, [permitTypeId, showSnackbar]);

  useEffect(() => {
    fetchSetup();
  }, [fetchSetup]);

  const handleRowChange = (index, field, value) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`${field}_${index}`]: "",
    }));
  };

  const handleDepartmentChange = (index, selectedOption) => {
    setRows((prevRows) =>
      prevRows.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              department_id: selectedOption ? selectedOption.value : "",
              department_name: selectedOption ? selectedOption.label : "",
            }
          : row
      )
    );
    setErrors((prevErrors) => ({
      ...prevErrors,
      [`department_id_${index}`]: "",
    }));
  };

  const handleAddRow = () => {
    setRows((prevRows) => [...prevRows, emptyApprovalRow()]);
  };

  const handleRemoveRow = (index) => {
    setRows((prevRows) =>
      prevRows.length > 1
        ? prevRows.filter((_, rowIndex) => rowIndex !== index)
        : prevRows
    );
  };

  const handleCancelClick = () => {
    navigate("/excavation-department-approval-setup");
  };

  const handleSaveClick = async () => {
    if (!permitOption?.value) {
      setSeverity("error");
      showSnackbar("Excavation permit type was not found.");
      return;
    }

    const validationErrors = validateApprovalRows(rows);
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      setSeverity("error");
      showSnackbar("Please fix the highlighted fields.");
      return;
    }

    try {
      setIsLoading(true);
      const authKey = localStorage.getItem("authKey");
      const payload = buildApprovalPayload(permitOption.value, rows);

      const response = await axios.put(
        `${API_URL}/api/permit/departments/setup/${permitOption.value}`,
        { departments: payload.departments },
        { headers: { Authorization: authKey } }
      );

      if (response.status === 200) {
        setSeverity("success");
        showSnackbar("Department approval setup updated successfully");
        setTimeout(() => navigate("/excavation-department-approval-setup"), 1000);
      }
    } catch (error) {
      console.error("Error updating excavation setup:", error.message);
      setSeverity("error");
      showSnackbar(
        error.response?.data?.message ||
          "An error occurred while updating department approvals."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <>
      <div
        className={`data-edit-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-edit-form-content">
          <div className="data-edit-form-header">
            <div className="data-edit-form-header-content">
              <div className="data-edit-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={handleCancelClick}
                  />
                </Tooltip>
                Edit Excavation Department Approval Setup
              </div>
              <div className="data-edit-form-heading-content-right-side"></div>
            </div>
          </div>
          <div className="header-separator"></div>
          <div className="data-edit-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form onKeyDown={handleKeyDown}>
                <div className="new-data-edit-form excavation-config-form">
                  <div className="form-group-select excavation-config-form-row">
                    <label>
                      Permit Type<span className="text-danger">*</span>
                    </label>
                    <Select
                      className="excavation-config-field"
                      options={permitOption ? [permitOption] : []}
                      value={permitOption}
                      isDisabled
                      placeholder="Excavation"
                    />
                  </div>

                  {rows.map((row, index) => (
                    <div
                      className="excavation-config-card"
                      key={`approval-row-${index}`}
                    >
                      <div className="excavation-config-card-header">
                        Department Approval {index + 1}
                      </div>

                      <div className="form-group-select excavation-config-card-row">
                        <label>
                          Department
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          className="excavation-config-field"
                          options={departmentOptions}
                          value={
                            departmentOptions.find(
                              (option) => option.value === row.department_id
                            ) || null
                          }
                          onChange={(selectedOption) =>
                            handleDepartmentChange(index, selectedOption)
                          }
                          placeholder="Select department"
                        />
                        {errors[`department_id_${index}`] && (
                          <div className="error-message">
                            {errors[`department_id_${index}`]}
                          </div>
                        )}
                      </div>

                      <div className="form-group excavation-config-card-row">
                        <label>
                          Approval Order
                          <span className="text-danger">*</span>
                        </label>
                        <div className="input-group excavation-config-input-group">
                          <input
                            className="excavation-config-input"
                            type="number"
                            min="1"
                            value={row.approval_order}
                            onChange={(event) =>
                              handleRowChange(
                                index,
                                "approval_order",
                                event.target.value
                              )
                            }
                            placeholder="Enter approval order"
                          />
                          {errors[`approval_order_${index}`] && (
                            <div className="error-message">
                              {errors[`approval_order_${index}`]}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="checkbox-form-group excavation-config-card-row">
                        <label className="checkbox-form-group-label">
                          Approval Type
                        </label>
                        <div className="fuel-checkbox-group">
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.is_required === "1"}
                              onChange={(event) =>
                                handleRowChange(
                                  index,
                                  "is_required",
                                  event.target.checked ? "1" : "0"
                                )
                              }
                              className="voyage-checkbox"
                            />
                            Required
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row.is_parallel === "1"}
                              onChange={(event) =>
                                handleRowChange(
                                  index,
                                  "is_parallel",
                                  event.target.checked ? "1" : "0"
                                )
                              }
                              className="voyage-checkbox"
                            />
                            Parallel
                          </label>
                        </div>
                        {errors[`is_parallel_${index}`] && (
                          <div className="error-message">
                            {errors[`is_parallel_${index}`]}
                          </div>
                        )}
                      </div>

                      {rows.length > 1 && (
                        <div className="excavation-config-card-actions">
                          <button
                            type="button"
                            className="excavation-config-remove-button"
                            onClick={() => handleRemoveRow(index)}
                          >
                            <IoCloseOutline /> Remove Department
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="form-group excavation-config-form-row">
                    <label></label>
                    <div className="input-group excavation-config-input-group">
                      <button
                        type="button"
                        className="data-edit-form-save-button"
                        onClick={handleAddRow}
                      >
                        Add Department
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
          <div className="footer">
            <div className="button-group">
              <button
                className="data-edit-form-save-button"
                type="button"
                onClick={handleSaveClick}
                disabled={isLoading || pageLoading}
              >
                {isLoading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-edit-form-cancel-button"
                type="button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default EditExcavationDepartmentApprovalSetup;
